import JSZip from 'jszip';
import type { SystemData } from '../services/reportEngine';
import {
  applyScanMetadata,
  decodeDiagnosticText,
  extractScanMetadata,
  type ScanMetadata,
} from './scanMetadata';

export type { ScanMetadata } from './scanMetadata';

/** Win32_Battery BatteryStatus → labels (read-only telemetry). */
const BATTERY_STATUS_MAP: Record<number, { status: string; powerSource: string }> = {
  1: { status: 'Running on Battery', powerSource: 'Running on Battery' },
  2: { status: 'AC Adapter Connected', powerSource: 'AC Adapter Connected' },
  3: { status: 'Fully Charged', powerSource: 'AC Adapter Connected' },
  4: { status: 'Low', powerSource: 'Running on Battery' },
  5: { status: 'Critical', powerSource: 'Running on Battery' },
  6: { status: 'Charging', powerSource: 'AC Adapter Connected' },
  7: { status: 'Charging and High', powerSource: 'AC Adapter Connected' },
  8: { status: 'Charging and Low', powerSource: 'AC Adapter Connected' },
  9: { status: 'Charging and Critical', powerSource: 'AC Adapter Connected' },
  10: { status: 'Unknown', powerSource: 'Unknown' },
  11: { status: 'Partially Charged', powerSource: 'AC Adapter Connected' },
};

function mapBatteryStatusCode(code: number | null | undefined): { status: string; powerSource: string } | null {
  if (code == null || code < 1) return null;
  return BATTERY_STATUS_MAP[code] ?? null;
}

/** Missing/unreported cycles use -1 (UI shows "Not Reported by Device"). */
function normalizeCycleCount(
  wmiValue?: number | null,
  htmlValue?: number
): number {
  if (wmiValue != null && wmiValue > 0) return wmiValue;
  if (htmlValue != null && htmlValue > 0) return htmlValue;
  return -1;
}

function isPortableChassis(
  chassis: PCCProExtras['chassis'] | undefined,
  meta: ScanMetadata,
  signals: { hasBatteryReport?: boolean; hasBatteryHardware?: boolean; hasCapacity?: boolean }
): boolean {
  if (chassis?.is_portable === true) return true;
  if (chassis?.form_factor === 'desktop' && chassis?.is_desktop === true) return false;
  if (chassis?.form_factor === 'laptop' || chassis?.form_factor === 'tablet') return true;
  const st = (meta.systemType || '').toLowerCase();
  if (st.includes('laptop') || st.includes('notebook') || st.includes('portable')) return true;
  if (signals.hasBatteryReport || signals.hasBatteryHardware || signals.hasCapacity) return true;
  return false;
}

export interface ParsedScanData {
  valid: boolean;
  scanId: string;
  timestamp: string;
  data: {
    system?: { 
      manufacturer?: string; 
      model?: string; 
      os_edition?: string; 
      os_build?: string; 
      uptime_days?: number; 
      system_type?: string;
      tpm?: string;
      secure_boot?: string | boolean;
    };
    cpu?: { 
      model?: string; 
      cores?: number; 
      load_pct?: number; 
      max_speed_ghz?: number;
      top_processes?: { name: string; cpu_sec: number }[]; 
    };
    ram?: { 
      total_gb?: number; 
      used_pct?: number; 
      available_gb?: number; 
      slots_used?: number; 
      slots_total?: number;
      slots_free?: number;
      slot_details?: { type: string }[];
      page_file_peak_mb?: number;
      top_processes?: { name: string; ram_mb: number }[]; 
    };
    storage?: { 
      volumes?: { used_pct: number; free_gb: number; total_gb: number }[]; 
      physical_disks?: { type: string; health: string }[]; 
      trim?: string;
      disk_error_count?: number;
      bitlocker?: string | boolean;
      temp_size_gb?: number;
      recycle_bin_gb?: number;
    };
    battery?: { 
      present?: boolean; 
      wear_pct?: number; 
      charge_pct?: number;
      status?: string;
      power_plan?: string;
    };
    network?: { 
      adapter?: string; 
      wifi?: { signal_pct?: number; link_speed_mbps?: number; band?: string };
      disconnects_7d?: number;
      wifi_driver?: { driver_date: string };
      gateway_reachable?: boolean;
      dns_servers?: string[];
    };
    startup?: { 
      program_count?: number; 
      boot_time_seconds?: number;
      programs?: { name: string; location: string }[]; 
      non_ms_service_count?: number;
      scheduled_tasks_count?: number;
    };
    drivers?: { 
      problem_count?: number; 
      outdated_count?: number; 
      usb_count?: number;
      unsigned_count?: number;
    };
    windows_health?: { 
      last_update_installed?: string;
      pending_reboot?: boolean;
      activation?: string;
      antivirus_count?: number;
      firewall_enabled?: boolean;
      sfc_status?: string;
      defender_signature_age_days?: number;
      update_service_status?: string;
    };
    stability?: { 
      bsod_count_90d?: number; 
      kernel_power_41_count?: number;
      app_crash_count_30d?: number;
      minidump_count?: number;
      critical_errors_30d?: number;
      error_events_30d?: number;
      service_crashes_30d?: number;
      recent_installs_14d?: number;
      reliability_trend?: { current: number; old: number; decline: number };
      thermal_events_count?: number;
    };
    display?: { 
      gpu_driver_date?: string; 
    };
    security?: { 
      defender_realtime?: boolean;
      tamper_protected?: boolean;
      last_scan_days_ago?: number;
      uac_level?: number;
      smartscreen?: string;
    };
  };
}

const ZIP_MODULE_NAMES = [
  'systeminfo.txt',
  'disk.txt',
  'ram.txt',
  'ipconfig.txt',
  'startup.txt',
  'events.txt',
  'battery-report.html',
  'msinfo32.txt',
] as const;

// v2.4 PRO enrichment JSON modules. Optional — older scanners don't ship them.
const ZIP_PRO_JSON_MODULES = [
  'top-processes.json',
  'browser-memory.json',
  'startup-apps.json',
  'installed-apps.json',
  'disk-cleanup.json',
  'defender.json',
  'windows-update.json',
  'oem.json',
  'battery-power.json',
  'chassis.json',
  'physical-memory.json',
  'physical-disks.json',
] as const;

/**
 * Pro enrichment data collected by scanner v2.4+. All fields optional.
 * UI must display "Not collected by this scanner version" when missing.
 */
export interface PCCProExtras {
  scannerVersion?: string;
  topProcesses?: {
    top_ram?: { name: string; instances: number; ram_mb: number; cpu_sec: number }[];
    top_cpu?: { name: string; instances: number; ram_mb: number; cpu_sec: number }[];
  };
  browserMemory?: {
    browsers: { browser: string; processes: number; total_ram_mb: number }[];
  };
  startupApps?: {
    count: number;
    items: { name: string; command: string; location: string; user?: string }[];
  };
  installedApps?: {
    count: number;
    items: { name: string; publisher?: string; install_date?: string; size_mb?: number; version?: string }[];
  };
  diskCleanup?: {
    temp_user_gb: number;
    temp_windows_gb: number;
    recycle_bin_gb: number;
    downloads_gb: number;
  };
  defender?: {
    defender_present: boolean;
    realtime_enabled: boolean | null;
    antivirus_enabled: boolean | null;
    tamper_protected: boolean | null;
    last_quick_scan?: string | null;
    last_full_scan?: string | null;
    signature_age_days?: number | null;
    registered_av_products?: { name: string; state_hex: string }[];
    antivirus_count?: number;
  };
  windowsUpdate?: {
    last_install_date?: string | null;
    pending_reboot?: boolean;
  };
  oem?: {
    manufacturer?: string | null;
    model?: string | null;
    serial?: string | null;
    bios_version?: string | null;
    bios_date?: string | null;
  };
  batteryPower?: {
    collected_at: string;
    battery_detected?: boolean;
    design_capacity?: number;
    full_charge_capacity?: number;
    cycle_count?: number | null;
    battery_status?: number;
    battery_status_label?: string;
    power_source?: string;
    status_label?: string;
    estimated_charge_remaining?: number;
    name?: string;
    chemistry?: string;
    events?: {
      low_battery?: any[];
      ac_unplugged?: any[];
      power_quality?: any[];
    };
  };
  chassis?: {
    collected_at?: string;
    chassis_types?: number[];
    form_factor?: 'laptop' | 'desktop' | 'tablet' | 'unknown';
    is_portable?: boolean;
    is_desktop?: boolean;
  };
  physicalMemory?: {
    module_count?: number;
    modules?: {
      capacity_mb?: number;
      speed_mhz?: number;
      configured_speed_mhz?: number;
      slot?: string;
      manufacturer?: string;
      part_number?: string;
      memory_type?: string;
      memory_type_code?: number;
    }[];
  };
  physicalDisks?: {
    disk_count?: number;
    disks?: {
      model?: string;
      media_type?: string;
      bus_type?: string;
      health_status?: string;
      size_gb?: number;
    }[];
  };
}

// --- Privacy / upload-safety constants ---------------------------------------
// MAX_ENTRY_BYTES caps the decompressed size of any single zip entry to defend
// against zip bombs (a tiny .pcc that expands to gigabytes). MAX_TOTAL_BYTES
// caps the cumulative decompressed payload we'll accept. ALLOWED_ZIP_ENTRIES
// is a strict allowlist — everything else (including path-traversal attempts
// like "..\foo" or absolute paths) is rejected before reading.
const MAX_ENTRY_BYTES = 4 * 1024 * 1024;   // 4 MB per entry
const MAX_TOTAL_BYTES = 20 * 1024 * 1024;  // 20 MB total decompressed
const ALLOWED_ZIP_ENTRIES = new Set<string>([
  'systeminfo.txt', 'disk.txt', 'ram.txt', 'ipconfig.txt',
  'startup.txt', 'events.txt', 'battery-report.html', 'msinfo32.txt',
  'top-processes.json', 'browser-memory.json', 'startup-apps.json',
  'installed-apps.json', 'disk-cleanup.json', 'defender.json',
  'windows-update.json', 'oem.json', 'battery-power.json',
  'chassis.json', 'physical-memory.json', 'physical-disks.json',
]);

function isUnsafeZipEntryName(name: string): boolean {
  if (!name) return true;
  if (name.includes('..')) return true;
  if (name.startsWith('/') || name.startsWith('\\')) return true;
  if (/^[a-zA-Z]:/.test(name)) return true; // drive letter / absolute Win path
  if (name.includes('\0')) return true;
  return false;
}

/** Mask a serial/service tag to keep only the last 4 chars: "******ABCD". */
export function maskSerial(raw?: string | null): string {
  if (!raw) return '';
  const s = String(raw).trim();
  if (!s || /^(to\s*be\s*filled|default\s*string|n\/a|none|null|unknown)$/i.test(s)) return '';
  if (s.length <= 4) return '*'.repeat(s.length);
  return '*'.repeat(Math.max(4, s.length - 4)) + s.slice(-4);
}

/**
 * Redact MAC addresses (and "Physical Address" lines) from ipconfig output.
 * Keeps the line structure for the parser/UI but replaces the value with
 * a masked form. Handles both XX-XX-XX-XX-XX-XX and XX:XX:XX:XX:XX:XX.
 */
export function redactMacAddresses(text: string): string {
  if (!text) return text;
  // Replace bare MAC tokens anywhere in the file.
  let out = text.replace(/\b([0-9A-Fa-f]{2}[-:]){5}[0-9A-Fa-f]{2}\b/g, 'XX-XX-XX-XX-XX-XX');
  // Replace "Physical Address" / "Adresse physique" values explicitly.
  out = out.replace(/(Physical Address[^\r\n:]*:\s*)\S+/gi, '$1XX-XX-XX-XX-XX-XX');
  return out;
}

// Track decompressed bytes for the active scan so we can enforce a global cap.
let _zipBytesConsumed = 0;
function _resetZipBudget() { _zipBytesConsumed = 0; }

async function readZipEntryText(zip: JSZip, name: string): Promise<string> {
  if (!ALLOWED_ZIP_ENTRIES.has(name)) return '';
  const entry = zip.file(name);
  if (!entry) return '';
  if (isUnsafeZipEntryName(entry.name)) return '';
  try {
    const buf = await entry.async('uint8array');
    if (buf.byteLength > MAX_ENTRY_BYTES) {
      console.warn(`[PCCUtils] rejecting oversized zip entry ${name} (${buf.byteLength} bytes)`);
      return '';
    }
    _zipBytesConsumed += buf.byteLength;
    if (_zipBytesConsumed > MAX_TOTAL_BYTES) {
      throw new Error('Scan file exceeds maximum decompressed size.');
    }
    let text = decodeDiagnosticText(buf);
    if (name === 'ipconfig.txt') text = redactMacAddresses(text);
    return text;
  } catch (err) {
    if (err instanceof Error && err.message.includes('maximum decompressed size')) throw err;
    return '';
  }
}

/**
 * Parse a .pcc file and extract diagnostic data.
 * Handles ZIP archive format containing multiple text/HTML diagnostic files.
 */
export async function parsePccFile(file: File): Promise<{
  valid: boolean;
  scanId: string;
  timestamp: string;
  scannerVersion: string;
  adminMode: boolean;
  moduleCount: number;
  manufacturer: string;
  model: string;
  osName: string;
  totalRamGb: number;
  diskTotalGb: number;
  diskFreeGb: number;
  data: Record<string, string>;
  rawModules: {
    systeminfo: string;
    ipconfig: string;
    disk: string;
    ram: string;
    startup: string;
    events: string;
    batteryReport: string;
    msinfo32: string;
  };
}> {

  // Step 1: Read file as ArrayBuffer to check format
  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);

  // Step 2: Check if this is a ZIP file (magic bytes PK = 0x50 0x4B)
  const isZip = bytes[0] === 0x50 && bytes[1] === 0x4B;

  if (!isZip) {
    // Check if it might be V1 text format (future format)
    const textContent = new TextDecoder().decode(bytes);
    if (textContent.trimStart().startsWith('PCCHECKR_SCAN_V')) {
      // V1 text format — parse differently (future support)
      throw new Error(
        'This scan file uses a newer format. Please download ' +
        'the latest scanner from pccheckr.com and scan again.'
      );
    }
    throw new Error(
      'This does not appear to be a PCCheckr scan file. ' +
      'Please upload the .pcc file created by the scanner.'
    );
  }

  // Step 3: Unzip and read all files (reset decompression budget first)
  _resetZipBudget();
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(arrayBuffer);
  } catch (e) {
    throw new Error(
      'Failed to read scan file. It may be corrupted. ' +
      'Please run the scanner again.'
    );
  }

  const systeminfo = await readZipEntryText(zip, 'systeminfo.txt');
  const ipconfig = await readZipEntryText(zip, 'ipconfig.txt');
  const disk = await readZipEntryText(zip, 'disk.txt');
  const ram = await readZipEntryText(zip, 'ram.txt');
  const startup = await readZipEntryText(zip, 'startup.txt');
  const events = await readZipEntryText(zip, 'events.txt');
  const batteryReport = await readZipEntryText(zip, 'battery-report.html');
  const msinfo32 = await readZipEntryText(zip, 'msinfo32.txt');

  // Verify we got at least systeminfo (minimum required)
  if (!systeminfo || systeminfo.length < 100) {
    throw new Error(
      'Scan file is missing system information. ' +
      'The scanner may not have finished. Please run it again.'
    );
  }

  const meta = extractScanMetadata(systeminfo, disk, ram);

  // Count modules found
  const moduleCount = [
    systeminfo, ipconfig, disk, ram, startup, events, batteryReport, msinfo32
  ].filter(content => content && content.length > 10).length;

  // Step 9: Generate scan ID from filename
  const fileNameMatch = file.name.match(/PCCheckr-(\w+)\.pcc/i);
  const scanId = fileNameMatch ? fileNameMatch[1] : 'unknown';

  return {
    valid: true,
    scanId,
    timestamp: new Date().toISOString(),
    scannerVersion: '1.0.0-STABLE',
    adminMode: true,
    moduleCount,
    manufacturer: meta.manufacturer,
    model: meta.model,
    osName: meta.osName,
    totalRamGb: meta.totalRamGb,
    diskTotalGb: meta.diskTotalGb,
    diskFreeGb: meta.diskFreeGb,
    data: {
      systeminfo,
      ipconfig,
      disk,
      ram,
      startup,
      events,
      batteryReport,
      msinfo32,
    },
    rawModules: {
      systeminfo,
      ipconfig,
      disk,
      ram,
      startup,
      events,
      batteryReport,
      msinfo32,
    },
  };
}


export interface PCCData {
  version: string;
  timestamp: string;
  scanId: string;
  system: {
    os: string;
    version: string;
    build: string;
    manufacturer: string;
    model: string;
    serialNumber: string;
    processor: string;
    ramGB: number;
    biosVersion: string;
    uptimeDays: number;
    installDate: string;
  };
  cpu?: {
    loadPercent: number;
    cores: number;
    logicalProcessors: number;
    clockSpeed: number;
    topProcesses: { name: string; cpu: number }[];
  };
  ram?: {
    totalGB: number;
    availableGB: number;
    usedPercent: number;
    type?: string;
    slotsUsed?: number;
    speed?: number;
    manufacturer?: string;
    topProcesses: { name: string; memMB: number }[];
  };
  storage: {
    mainDrive: {
      model: string;
      sizeGB: number;
      freeGB: number;
      type?: string; // SSD/HDD/NVMe
      status: string; // SMART
    };
    volumes: { letter: string; label: string; freeGB: number; totalGB: number }[];
    bitLocker: string;
    diskErrors: number;
  };
  battery?: {
    present: boolean;
    designCapacity: number;
    fullChargeCapacity: number;
    wearPercent: number;
    cycleCount: number;
    status: string;
    acStatus: string;
  };
  network?: {
    adapters: { name: string; status: string; ip: string; macSuffix: string; speed: string }[];
    wifi?: { ssid: string; signal: number; channel: number };
    gatewayReachability: boolean;
    recentDisconnects: number;
  };
  security: {
    defender: string;
    firewall: string;
    secureBoot: string;
    tpm: string;
    bitLocker: string;
    uac: string;
    rdp: string;
    smbv1: string;
    executionPolicy: string;
    adminCount: number;
  };
  windows: {
    updateStatus: string;
    pendingReboot: boolean;
    activation: string;
    pendingUpdates: number;
  };
  stability: {
    bsodCount: number;
    unexpectedShutdowns: number;
    appCrashes: number;
    appHangs: number;
    minidumpCount: number;
    reliabilityIndex: number;
  };
  drivers: {
    problemDevices: string[];
    outdatedCount: number;
    unsignedCount: number;
  };
  startup: {
    items: { name: string; path: string; impact: string }[];
    nonMicrosoftServices: number;
  };
  gpu?: {
    name: string;
    driverVersion: string;
    vramGB?: number;
    resolution: string;
    refreshRate: number;
  };
  software?: {
    officeInstalled: boolean;
    officeVersion?: string;
    recentOfficeCrashes: number;
  };
  // Phase 1 Expansion Fields
  powerPlan?: string;
  startupCount?: number;
  tempSizeGB?: number;
  recycleBinSizeGB?: number;
  defenderSigAge?: number;
  ramSlotsFree?: number;
  ramSlotsTotal?: number;
  bootTimeSec?: number;
  rawModules?: {
    systeminfo: string;
    ipconfig: string;
    disk: string;
    ram: string;
    startup: string;
    events: string;
    batteryReport: string;
    msinfo32: string;
  };
  // Legacy support for early Phase 1
  diagnostics?: {
    uptimeDays: number;
    batteryWear: number | null;
    wifiDriverDate: string;
    bsodCount: number;
    startupAppsCount: number;
  };
  // v2.4 Pro enrichment (optional)
  proExtras?: PCCProExtras;
}

export class PCCUtils {
  static readonly HEADER = "PCCHECKR_SCAN_V1";
  static readonly LEGACY_PRO_HEADER = "PCCHECKR_SCAN_PRO_V1";
  static readonly DATA_START = "---BEGIN DIAGNOSTIC DATA---";
  static readonly DATA_END = "---END DIAGNOSTIC DATA---";

  /**
   * Checks if a .pcc file is from a legacy (pre-2.0) scanner
   */
  static isLegacyFormat(content: string): boolean {
    const trimmed = content.trim();
    if (trimmed.startsWith(this.LEGACY_PRO_HEADER)) return true;
    if (!trimmed.includes("SCANNER_VERSION:")) return true;
    return false;
  }

  /**
   * Parses header metadata lines from the .pcc file
   */
  private static parseHeaders(content: string): Record<string, string> {
    const headers: Record<string, string> = {};
    const lines = content.split('\n');
    for (const line of lines) {
      const trimLine = line.trim();
      // Stop at data marker
      if (trimLine.startsWith('---BEGIN')) break;
      const colonIdx = trimLine.indexOf(':');
      if (colonIdx > 0) {
        const key = trimLine.substring(0, colonIdx).trim();
        const value = trimLine.substring(colonIdx + 1).trim();
        headers[key] = value;
      }
    }
    return headers;
  }

  /**
   * Computes SHA-256 hex digest of a string
   */
  private static async sha256(text: string): Promise<string> {
    const msgUint8 = new TextEncoder().encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Decodes a .pcc file content into JSON data
   */
  static async decode(content: string | ArrayBuffer): Promise<PCCData> {
    // 1. Detect format by inspecting text header FIRST
    let textContent = "";
    let isZip = false;

    if (content instanceof ArrayBuffer) {
      textContent = new TextDecoder().decode(content);
    } else {
      textContent = content.trim();
    }


    const isV1 = textContent.startsWith(this.HEADER) || textContent.startsWith(this.LEGACY_PRO_HEADER);
    
    if (isV1) {
      isZip = false;
    } else {
      // Only check for ZIP if not a V1 text file
      if (content instanceof ArrayBuffer) {
        const view = new Uint8Array(content);
        if (view[0] === 0x50 && view[1] === 0x4B) { // 'PK'
          isZip = true;
        }
      } else if (textContent.startsWith('PK\x03\x04')) {
        isZip = true;
      }
    }

    // 2. Handle ZIP format if detected
    if (isZip) {
      const buffer = content instanceof ArrayBuffer ? content : this.stringToBuffer(content as string);
      return this.decodeZip(buffer);
    }

    // 3. Handle Text/Base64 format (V1)
    if (!textContent.startsWith(this.HEADER) && !textContent.startsWith(this.LEGACY_PRO_HEADER)) {
      throw new Error("This does not look like a valid PCCheckr scan file. Please run the scanner again.");
    }

    // Determine data markers — support both new and legacy formats
    let dataStart = textContent.indexOf(this.DATA_START);
    let dataEnd = textContent.indexOf(this.DATA_END);
    let dataStartMarker = this.DATA_START;

    // Fallback to legacy markers
    if (dataStart === -1 || dataEnd === -1) {
      dataStart = textContent.indexOf("---BEGIN DATA---");
      dataEnd = textContent.indexOf("---END DATA---");
      dataStartMarker = "---BEGIN DATA---";
    }

    if (dataStart === -1 || dataEnd === -1 || dataEnd <= dataStart) {
      throw new Error("Invalid file structure. The file may be truncated or corrupted.");
    }

    const base64Payload = textContent.substring(dataStart + dataStartMarker.length, dataEnd).trim();

    // Parse header metadata for checksum verification
    const headers = this.parseHeaders(textContent);
    const checksumAlgo = headers['CHECKSUM_ALGORITHM'];
    const providedChecksum = headers['CHECKSUM'];

    if (checksumAlgo === 'SHA-256' && providedChecksum) {
      const calculatedChecksum = await this.sha256(base64Payload);
      if (providedChecksum !== calculatedChecksum) {
        console.warn("SHA-256 checksum mismatch. Data may have been corrupted.");
      }
    }

    try {
      const binary = atob(base64Payload);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const jsonString = new TextDecoder().decode(bytes);
      const json = JSON.parse(jsonString);
      return this.normalize(json);
    } catch (e: any) {
      console.error("Decoding error:", e);
      throw new Error("Failed to decode scan data. Please run the scanner again.");
    }
  }

  /**
   * Helper to convert string to ArrayBuffer (for ZIP handling if passed as string)
   */
  private static stringToBuffer(str: string): ArrayBuffer {
    const bytes = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i);
    return bytes.buffer;
  }

  /**
   * Decodes a ZIP-based .pcc file
   */
  private static async decodeZip(buffer: ArrayBuffer): Promise<PCCData> {
    try {
      _resetZipBudget();
      const zip = await JSZip.loadAsync(buffer);

      const files: Record<string, string> = {};
      for (const name of ZIP_MODULE_NAMES) {
        files[name] = await readZipEntryText(zip, name);
      }

      if (!files['systeminfo.txt']) {
        throw new Error("Missing critical diagnostic data (systeminfo.txt).");
      }

      // v2.4 optional JSON modules. Older scanners simply don't contain them.
      const proExtras: PCCProExtras = {};
      const safeParse = async (entry: string, assign: (val: any) => void) => {
        let text = '';
        try { text = await readZipEntryText(zip, entry); }
        catch (e) {
          // readZipEntryText only throws on zip-bomb conditions; surface it.
          console.warn(`[PCCUtils] skipped ${entry} (read failed):`, e);
          return;
        }
        if (!text) return;
        try {
          const parsed = JSON.parse(text);
          // Defensive: optional modules are always objects; ignore arrays/scalars.
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            assign(parsed);
          }
        } catch (e) {
          console.warn(`[PCCUtils] failed to parse ${entry}:`, e);
        }
      };
      await safeParse('top-processes.json',  v => proExtras.topProcesses = v);
      await safeParse('browser-memory.json', v => proExtras.browserMemory = v);
      await safeParse('startup-apps.json',   v => proExtras.startupApps = v);
      await safeParse('installed-apps.json', v => proExtras.installedApps = v);
      await safeParse('disk-cleanup.json',   v => proExtras.diskCleanup = v);
      await safeParse('defender.json',       v => proExtras.defender = v);
      await safeParse('windows-update.json', v => proExtras.windowsUpdate = v);
      await safeParse('oem.json',            v => {
        // Privacy: mask the BIOS serial / service tag before it enters memory.
        if (v && typeof v === 'object' && 'serial' in v) v.serial = maskSerial(v.serial);
        proExtras.oem = v;
      });
      await safeParse('battery-power.json',  v => proExtras.batteryPower = v);
      await safeParse('chassis.json',        v => proExtras.chassis = v);
      await safeParse('physical-memory.json', v => proExtras.physicalMemory = v);
      await safeParse('physical-disks.json', v => proExtras.physicalDisks = v);

      const decoded = this.parseRawFiles(files, proExtras);
      if (Object.keys(proExtras).length > 0) {
        decoded.proExtras = proExtras;
      }
      return decoded;
    } catch (e: any) {
      console.error("ZIP Decoding error:", e);
      throw new Error(`Failed to read ZIP-based .pcc: ${e.message}`);
    }
  }

  /**
   * Extracts battery health metrics from powercfg /batteryreport HTML.
   */
  private static parseBatteryReport(html: string) {
    if (!html || html.length < 100) return null;

    const extract = (label: string) => {
      // More flexible regex: label followed by anything up to a <td> containing digits
      const regex = new RegExp(`${label}.*?<td[^>]*>([\\d,\\s.]+)`, 'i');
      const match = html.match(regex);
      if (match?.[1]) {
        // Handle localized numbers (remove non-digits except decimal points if needed, 
        // though battery report usually uses integers)
        return parseInt(match[1].replace(/[^0-9]/g, ''), 10);
      }
      return 0;
    };

    const designCapacity = extract('Design Capacity');
    const fullChargeCapacity = extract('Full Charge Capacity');
    const cycleRaw = extract('Cycle Count');
    const cycleCount = cycleRaw > 0 ? cycleRaw : -1;

    const acMatch = html.match(/Power Source.*?<td[^>]*>(AC|Plugged|Secteur|Red)/i);
    const batMatch = html.match(/Power Source.*?<td[^>]*>(Battery|Batterie|Batería)/i);
    const acStatus = acMatch ? 'AC Adapter Connected' : (batMatch ? 'Running on Battery' : 'Unknown');

    if (!designCapacity && !fullChargeCapacity) {
      console.warn('[PCCUtils] Battery report found but no capacities extracted.');
      return null;
    }

    const wearPercent = designCapacity > 0 
      ? Math.max(0, Math.min(100, Math.round((1 - (fullChargeCapacity / designCapacity)) * 100)))
      : 0;

    const batteryData = {
      present: true,
      designCapacity,
      fullChargeCapacity,
      wearPercent,
      cycleCount,
      status: fullChargeCapacity > 0 ? 'Fully Charged' : 'Present',
      acStatus,
    };

    return batteryData;
  }

  /**
   * Parses raw diagnostic text files into PCCData
   */
  private static parseRawFiles(files: Record<string, string>, proExtras?: PCCProExtras): PCCData {
    const sys = files['systeminfo.txt'] || '';
    const disk = files['disk.txt'] || '';
    const ram = files['ram.txt'] || '';
    const meta = extractScanMetadata(sys, disk, ram);

    const getVal = (text: string, key: string) => {
      if (!text) return '';
      const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const patterns = [
        new RegExp(`^${escapedKey}\\s*:\\s*(.+)$`, 'im'),
        new RegExp(`${escapedKey}\\s*:\\s*(.+)`, 'i'),
      ];
      for (const re of patterns) {
        const match = text.match(re);
        if (match?.[1]?.trim()) return match[1].trim();
      }
      return '';
    };

    const ramAvailStr = getVal(sys, 'Available Physical Memory') || getVal(sys, 'Verfügbarer physischer Speicher') || getVal(sys, 'Mémoire physique disponible');
    const parseMB = (s: string) => {
      if (!s) return 0;
      const match = s.match(/([\d.,\s]+)\s*MB/i);
      const valStr = match ? match[1] : s;
      // Handle localized numbers (thousands separator removal)
      const normalized = valStr.replace(/(\d)[.,](\d{3})/g, '$1$2').replace(/[^\d.,]/g, '').replace(',', '.');
      return Math.round(parseFloat(normalized)) || 0;
    };
    const totalMB = meta.totalRamGb > 0 ? Math.round(meta.totalRamGb * 1024) : parseMB(getVal(sys, 'Total Physical Memory') || getVal(sys, 'Gesamter physikalischer Speicher'));
    const availMB = parseMB(ramAvailStr);
    const usedMB = Math.max(0, totalMB - availMB);
    const usedPct = totalMB > 0 ? Math.min(100, Math.round((usedMB / totalMB) * 100)) : 0;

    const data: any = {
      version: "1.0.0-ZIP",
      timestamp: new Date().toISOString(),
      scanId: "ZIP-" + Math.random().toString(36).substring(2, 10).toUpperCase(),
      system: {
        os: meta.osName,
        version: getVal(sys, 'OS Version'),
        build: getVal(sys, 'OS Build Type') || getVal(sys, 'OS Build') || 'N/A',
        manufacturer: meta.manufacturer,
        model: meta.model,
        // Privacy: only the masked form is ever stored/displayed/passed downstream.
        serialNumber: maskSerial(getVal(sys, 'BIOS Serial Number') || getVal(sys, 'Serial Number')),
        processor: getVal(sys, 'Processor(s)').split('[01]:')[1]?.trim() || getVal(sys, 'Processor(s)'),
        ramGB: meta.totalRamGb || Math.round(totalMB / 1024),
        biosVersion: getVal(sys, 'BIOS Version') || '',
        uptimeDays: 0,
        installDate: getVal(sys, 'Original Install Date') || '',
      },
      cpu: {
        loadPercent: 0,
        cores: 0,
        logicalProcessors: 0,
        clockSpeed: 0,
        topProcesses: []
      },
      ram: {
        totalGB: meta.totalRamGb || Math.round(totalMB / 1024),
        availableGB: Math.round(availMB / 1024 * 10) / 10,
        usedPercent: usedPct,
        type: "DDRx",
        slotsUsed: meta.ramSlotsUsed || 1,
        topProcesses: []
      },
      storage: {
        mainDrive: {
          model: "System Drive",
          sizeGB: meta.diskTotalGb,
          freeGB: meta.diskFreeGb,
          status: meta.diskTotalGb > 0 && meta.diskFreeGb < 20 ? 'Near Capacity' : 'Healthy',
        },
        volumes: [],
        bitLocker: "Unknown",
        diskErrors: 0
      },
      security: {
        defender: "Unknown",
        firewall: "Enabled",
        secureBoot: "Unknown",
        tpm: "Unknown",
        bitLocker: "Unknown",
        uac: "Unknown",
        rdp: "Unknown",
        smbv1: "Unknown",
        executionPolicy: "Unknown",
        adminCount: 1
      },
      windows: {
        updateStatus: "Unknown",
        pendingReboot: false,
        activation: "Unknown",
        pendingUpdates: 0
      },
      stability: {
        bsodCount: 0,
        unexpectedShutdowns: 0,
        appCrashes: 0,
        appHangs: 0,
        minidumpCount: 0,
        reliabilityIndex: 8
      },
      drivers: {
        problemDevices: [],
        outdatedCount: 0,
        unsignedCount: 0
      },
      battery: (() => {
        const pro = proExtras?.batteryPower;
        const chassis = proExtras?.chassis;
        const batReportHtml = files['battery-report.html'] || '';
        const parsed = this.parseBatteryReport(batReportHtml);

        const hasBatReport = !!batReportHtml && batReportHtml.length > 500 && !/no battery report/i.test(batReportHtml);
        const hasBatteryHardware = !!(pro?.battery_detected || pro?.battery_status != null || pro?.design_capacity || pro?.full_charge_capacity);
        const hasCapacity = !!(parsed && (parsed.designCapacity > 0 || parsed.fullChargeCapacity > 0)) ||
          !!(pro?.design_capacity || pro?.full_charge_capacity);

        const isLaptop = isPortableChassis(chassis, meta, {
          hasBatteryReport: hasBatReport,
          hasBatteryHardware,
          hasCapacity,
        });

        const statusFromCode = mapBatteryStatusCode(pro?.battery_status);
        const cycleCount = normalizeCycleCount(pro?.cycle_count, parsed?.cycleCount);

        const resolvePowerSource = (): string => {
          if (pro?.power_source) return pro.power_source;
          if (statusFromCode?.powerSource) return statusFromCode.powerSource;
          if (pro?.battery_status != null) return mapBatteryStatusCode(pro.battery_status)?.powerSource || 'Unknown';
          if (parsed?.acStatus && parsed.acStatus !== 'Unknown') return parsed.acStatus;
          return 'Unknown';
        };

        const resolveStatus = (): string => {
          if (pro?.battery_status_label) return pro.battery_status_label;
          if (statusFromCode?.status) return statusFromCode.status;
          if (pro?.status_label && pro.status_label !== 'OK') return pro.status_label;
          return parsed?.status || 'Present';
        };

        const buildBattery = (dCap: number, fCap: number) => ({
          present: true,
          designCapacity: dCap,
          fullChargeCapacity: fCap,
          wearPercent: dCap > 0 ? Math.max(0, Math.min(100, Math.round((1 - (fCap / dCap)) * 100))) : 0,
          cycleCount,
          status: resolveStatus(),
          acStatus: resolvePowerSource(),
          chargePercent: pro?.estimated_charge_remaining ?? undefined,
        });

        if (pro && (hasCapacity || pro.battery_status != null || pro.battery_detected)) {
          const dCap = pro.design_capacity || parsed?.designCapacity || 0;
          const fCap = pro.full_charge_capacity || parsed?.fullChargeCapacity || 0;
          if (dCap > 0 || fCap > 0 || pro.battery_status != null) {
            return buildBattery(dCap, fCap);
          }
        }

        if (parsed) {
          return {
            ...parsed,
            present: true,
            cycleCount: normalizeCycleCount(undefined, parsed.cycleCount),
            acStatus: parsed.acStatus !== 'Unknown' ? parsed.acStatus : resolvePowerSource(),
          };
        }

        return {
          present: isLaptop,
          designCapacity: 0,
          fullChargeCapacity: 0,
          wearPercent: 0,
          cycleCount: -1,
          status: isLaptop ? 'Battery Data Not Available' : 'Not Detected',
          acStatus: isLaptop && hasBatteryHardware ? resolvePowerSource() : 'Unknown',
        };
      })()
    };

    if (proExtras?.chassis) {
      (data.system as Record<string, unknown>).chassisFormFactor = proExtras.chassis.form_factor;
      (data.system as Record<string, unknown>).isPortable = proExtras.chassis.is_portable;
    }

    if (proExtras?.physicalMemory?.modules?.length) {
      const mods = proExtras.physicalMemory.modules;
      const types = [...new Set(mods.map(m => m.memory_type).filter(Boolean))];
      if (types.length === 1) data.ram.type = types[0] as string;
      data.ram.slotsUsed = mods.length;
      data.ram.modules = mods;
    }

    if (proExtras?.physicalDisks?.disks?.length) {
      const disks = proExtras.physicalDisks.disks;
      data.storage.physicalDisks = disks.map(d => ({
        model: d.model || 'Disk',
        mediaType: d.media_type || 'Unknown',
        busType: d.bus_type || 'Unknown',
        health: d.health_status || 'Unknown',
        sizeGB: d.size_gb ?? 0,
      }));
      const primary = disks[0];
      if (primary) {
        const media = (primary.media_type || '').toUpperCase();
        if (media.includes('SSD') || media === '4') data.storage.mainDrive.type = 'SSD';
        else if (media.includes('HDD') || media === '3') data.storage.mainDrive.type = 'HDD';
        else if (media.includes('NVME') || (primary.bus_type || '').toUpperCase().includes('NVME')) {
          data.storage.mainDrive.type = 'NVMe SSD';
        }
        if (primary.model) data.storage.mainDrive.model = primary.model;
        if (primary.health_status) data.storage.mainDrive.status = primary.health_status;
        if (primary.size_gb) data.storage.mainDrive.sizeGB = primary.size_gb;
      }
    }

    data.rawModules = {
      systeminfo: sys,
      ipconfig: files['ipconfig.txt'] || '',
      disk,
      ram,
      startup: files['startup.txt'] || '',
      events: files['events.txt'] || '',
      batteryReport: files['battery-report.html'] || '',
      msinfo32: files['msinfo32.txt'] || '',
    };

    applyScanMetadata(data, meta);
    return this.normalize(data);
  }

  /**
   * Maps normalized PCCData to flat SystemData for ReportEngine / ReportPage.
   */
  static toSystemData(pcc: PCCData): SystemData {
    const raw = pcc.rawModules;
    const meta = raw
      ? extractScanMetadata(raw.systeminfo, raw.disk, raw.ram)
      : extractScanMetadata('', '', '');

    const sys = pcc.system;
    const ram = pcc.ram;
    const storage = pcc.storage;

    const pick = (primary: string | undefined, fallback: string, unknownLabel: string) => {
      const p = primary?.trim();
      if (p && p !== unknownLabel) return p;
      return fallback;
    };

    const manufacturer = pick(sys.manufacturer, meta.manufacturer, 'Unknown Manufacturer');
    const model = pick(sys.model, meta.model, 'Unknown Model');
    const os = pick(sys.os, meta.osName, 'Windows');
    const ramGB =
      sys.ramGB > 0 ? sys.ramGB : meta.totalRamGb > 0 ? meta.totalRamGb : ram?.totalGB ?? 0;
    const sizeGB =
      storage?.mainDrive?.sizeGB > 0 ? storage.mainDrive.sizeGB : meta.diskTotalGb;
    const freeGB =
      storage?.mainDrive?.freeGB > 0 ? storage.mainDrive.freeGB : meta.diskFreeGb;

    return {
      os,
      version: sys.version || '',
      build: sys.build || 'N/A',
      manufacturer,
      model,
      serialNumber: sys.serialNumber || '',
      processor: sys.processor || 'Unknown Processor',
      ramGB,
      biosVersion: sys.biosVersion || '',
      uptimeDays: sys.uptimeDays ?? 0,
      installDate: sys.installDate || '',
      cpu: pcc.cpu,
      ram: ram
        ? {
            freeGB: ram.availableGB ?? ram.totalGB ?? ramGB,
            availableGB: ram.availableGB,
            usedPercent: ram.usedPercent ?? 0,
            type: ram.type,
            slotsUsed: ram.slotsUsed || meta.ramSlotsUsed || 0,
            topProcesses: ram.topProcesses ?? [],
          }
        : {
            freeGB: 0,
            availableGB: 0,
            usedPercent: 0,
            slotsUsed: meta.ramSlotsUsed,
            topProcesses: [],
          },
      storage: {
        mainDrive: {
          model: storage?.mainDrive?.model || 'System Drive',
          sizeGB,
          freeGB,
          status: storage?.mainDrive?.status || (sizeGB > 0 ? 'Healthy' : 'Unknown'),
        },
        bitLocker: storage?.bitLocker || 'Unknown',
        diskErrors: storage?.diskErrors ?? 0,
      },
      battery: pcc.battery,
      network: pcc.network,
      security: pcc.security,
      windows: pcc.windows,
      stability: pcc.stability,
      drivers: pcc.drivers,
      software: pcc.software,
      rawModules: raw
        ? {
            systeminfo: raw.systeminfo,
            ipconfig: raw.ipconfig,
            disk: raw.disk,
            ram: raw.ram,
            startup: raw.startup,
            events: raw.events,
            batteryReport: raw.batteryReport,
          }
        : undefined,
    };
  }

  /**
   * Normalizes scanner data to ensure consistency across versions and handle missing fields
   */
  static normalize(raw: any): PCCData {
    // Deep clone to avoid mutations
    const data = JSON.parse(JSON.stringify(raw));

    if (data.rawModules?.systeminfo) {
      const meta = extractScanMetadata(
        data.rawModules.systeminfo,
        data.rawModules.disk || '',
        data.rawModules.ram || ''
      );
      applyScanMetadata(data, meta);
    }

    // Ensure system structure
    if (!data.system) data.system = {};
    // Privacy: re-mask in case raw input still contains the full serial.
    if (data.system.serialNumber) data.system.serialNumber = maskSerial(data.system.serialNumber);
    data.system.os = data.system.os || 'Windows (Unknown Version)';
    if (!data.system.manufacturer?.trim()) data.system.manufacturer = 'Unknown Manufacturer';
    if (!data.system.model?.trim()) data.system.model = 'Unknown Model';
    data.system.processor = data.system.processor || 'Unknown Processor';
    data.system.ramGB = Number(data.system.ramGB || data.system.ram_gb || 0);
    data.system.uptimeDays = Number(data.system.uptimeDays || data.system.uptime_days || 0);

    // Ensure RAM structure - Handle variants
    const ramObj = data.ram || data.memory || {};
    const ramTotal = Number(
      data.system?.ramGB ||
        ramObj.total_gb ||
        ramObj.totalGB ||
        ramObj.ram_total_gb ||
        ramObj.ramGB ||
        data.system?.ram_gb ||
        0
    );
    const ramAvailable = Number(ramObj.available_gb || ramObj.freeGB || ramObj.free_gb || ramObj.availableGB || 0);
    const ramUsedPct = Number(ramObj.used_pct || ramObj.usedPercent || 0);

    data.ram = {
      totalGB: ramTotal,
      availableGB: ramAvailable,
      usedPercent: ramUsedPct,
      type: ramObj.type || (ramTotal > 0 ? 'DDRx' : ''),
      slotsUsed: Number(ramObj.slots_used || ramObj.slotsUsed || 0),
      topProcesses: ramObj.top_processes || ramObj.topProcesses || []
    };

    // Ensure CPU structure
    if (!data.cpu) data.cpu = {};
    data.cpu.loadPercent = Number(data.cpu.loadPercent || data.cpu.load_pct || 0);
    if (!data.cpu.topProcesses) data.cpu.topProcesses = data.cpu.topProcesses || data.cpu.top_processes || [];

    // Ensure Storage structure - Handle variants
    const storageObj = data.storage || {};
    const mainDriveRaw = storageObj.mainDrive || storageObj.main_drive || {};
    
    const sizeGB = Number(mainDriveRaw.sizeGB || mainDriveRaw.size_gb || mainDriveRaw.totalGB || mainDriveRaw.total_gb || mainDriveRaw.totalCapacity || storageObj.total_gb || 0);
    const freeGB = Number(mainDriveRaw.freeGB || mainDriveRaw.free_gb || mainDriveRaw.freeSpace || storageObj.free_gb || 0);

    data.storage = {
      ...storageObj,
      mainDrive: {
        model: mainDriveRaw.model || 'System Drive',
        sizeGB: sizeGB,
        freeGB: freeGB,
        status: mainDriveRaw.status || (sizeGB > 0 ? (freeGB < 20 ? 'Near Capacity' : 'Healthy') : 'Unknown')
      }
    };

    // Fallback: If mainDrive size is missing, try to find C: drive in volumes
    if (data.storage.mainDrive.sizeGB === 0 && (data.storage.volumes || data.storage.volumes_list)) {
      const volumes = data.storage.volumes || data.storage.volumes_list || [];
      const cDrive = volumes.find((v: any) => v.letter === 'C:' || v.letter === 'C' || v.deviceID === 'C:');
      if (cDrive) {
        data.storage.mainDrive.sizeGB = Number(cDrive.totalGB || cDrive.total_gb || cDrive.sizeGB || 0);
        data.storage.mainDrive.freeGB = Number(cDrive.freeGB || cDrive.free_gb || cDrive.freeSpace || 0);
      }
    }

    // Ensure Security structure
    if (!data.security) data.security = {};
    data.security.defender = data.security.defender || 'Unknown';
    data.security.firewall = data.security.firewall || 'Unknown';
    data.security.secureBoot = data.security.secureBoot || 'Unknown';
    data.security.activation = data.security.activation || 'Unknown';

    // Ensure Windows structure
    if (!data.windows) data.windows = {};
    data.windows.updateStatus = data.windows.updateStatus || 'Unknown';

    // Ensure Stability structure
    if (!data.stability) data.stability = {};
    data.stability.bsodCount = Number(data.stability.bsodCount || 0);
    data.stability.reliabilityIndex = Number(data.stability.reliabilityIndex || 0);

    // Ensure Drivers structure
    if (!data.drivers) data.drivers = {};
    if (!data.drivers.problemDevices) data.drivers.problemDevices = [];
    data.drivers.outdatedCount = Number(data.drivers.outdatedCount || 0);

    // Phase 1 Expansion Fields
    data.startupCount = Number(data.startupCount || 0);
    data.tempSizeGB = Number(data.tempSizeGB || 0);
    data.recycleBinSizeGB = Number(data.recycleBinSizeGB || 0);
    data.defenderSigAge = Number(data.defenderSigAge || 0);
    data.ramSlotsFree = Number(data.ramSlotsFree || 0);
    data.ramSlotsTotal = Number(data.ramSlotsTotal || 0);
    data.bootTimeSec = Number(data.bootTimeSec || 0);

    // Ensure Battery structure
    const batObj = data.battery || {};
    const rawCycle = batObj.cycleCount;
    const cycleCount =
      rawCycle != null && Number(rawCycle) > 0 ? Number(rawCycle) : -1;
    const hasPowerTelemetry =
      batObj.acStatus &&
      batObj.acStatus !== 'Unknown' &&
      batObj.acStatus !== 'Not Detected';
    data.battery = {
      present: !!(
        batObj.present ||
        (batObj.designCapacity && batObj.designCapacity > 0) ||
        (batObj.fullChargeCapacity && batObj.fullChargeCapacity > 0) ||
        hasPowerTelemetry ||
        (batObj.status && batObj.status !== 'Not Detected' && batObj.status !== 'Battery Data Not Available')
      ),
      designCapacity: Number(batObj.designCapacity || 0),
      fullChargeCapacity: Number(batObj.fullChargeCapacity || 0),
      wearPercent: Number(batObj.wearPercent || 0),
      cycleCount,
      status: batObj.status || 'Unknown',
      acStatus: batObj.acStatus || 'Unknown',
    };

    return data as PCCData;
  }

  /**
   * Generates a .pcc file content for testing
   */
  static async generateMock(data: Partial<PCCData> = {}): Promise<string> {
    const defaultData: PCCData = {
      version: "1.2.0",
      timestamp: new Date().toISOString(),
      scanId: "PCC-MOCK8A2F",
      system: {
        os: "Windows 11 Pro",
        version: "23H2",
        build: "22631",
        manufacturer: "Dell Inc.",
        model: "XPS 15 9520",
        serialNumber: "SVC-TAG-MOCK",
        processor: "Intel Core i7-12700H",
        ramGB: 16,
        biosVersion: "1.15.0",
        uptimeDays: 9,
        installDate: "2023-01-01"
      },
      storage: {
        mainDrive: {
          model: "NVMe Samsung 1TB",
          sizeGB: 512,
          freeGB: 14.5,
          status: "Healthy"
        },
        volumes: [],
        bitLocker: "On",
        diskErrors: 0
      },
      security: {
        defender: "Active",
        firewall: "Enabled",
        secureBoot: "On",
        tpm: "2.0 Ready",
        bitLocker: "Encrypted",
        uac: "Level 3",
        rdp: "Disabled",
        smbv1: "Disabled",
        executionPolicy: "RemoteSigned",
        adminCount: 1
      },
      windows: {
        updateStatus: "Up to date",
        pendingReboot: false,
        activation: "Activated",
        pendingUpdates: 0
      },
      stability: {
        bsodCount: 0,
        unexpectedShutdowns: 0,
        appCrashes: 0,
        appHangs: 0,
        minidumpCount: 0,
        reliabilityIndex: 10
      },
      drivers: {
        problemDevices: [],
        outdatedCount: 0,
        unsignedCount: 0
      },
      startup: {
        items: [],
        nonMicrosoftServices: 10
      },
      powerPlan: "Power Saver",
      startupCount: 22,
      tempSizeGB: 3.4,
      recycleBinSizeGB: 1.2,
      defenderSigAge: 12,
      ramSlotsFree: 1,
      ramSlotsTotal: 2,
      bootTimeSec: 72.5,
      ...data
    };

    const jsonString = JSON.stringify(defaultData);
    const base64 = btoa(jsonString);
    const hash = await this.sha256(base64);
    const ts = new Date().toISOString();

    return [
      this.HEADER,
      'SCANNER_VERSION:2.0',
      `SCAN_ID:${defaultData.scanId}`,
      `SCAN_TIMESTAMP:${ts}`,
      'CHECKSUM_ALGORITHM:SHA-256',
      `CHECKSUM:${hash}`,
      this.DATA_START,
      base64,
      this.DATA_END
    ].join('\n');
  }

  /**
   * Downloads a mock .pcc file
   */
  static async downloadMock() {
    const content = await this.generateMock();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'PCCheckr-PCC-MOCK8A2F.pcc';
    a.click();
    URL.revokeObjectURL(url);
  }
}
